import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import { shiftApi } from '../../../api/shiftApi';
import { toast } from 'react-toastify';
import { formatTime12Hour } from '../../../utils/timeUtils';

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

type SortDirection = 'asc' | 'desc';
type SortKey = 'employeeName' | 'shiftName' | 'workWeekName' | 'startTime' | 'endTime' | 'effectiveDate';

const sortableColumns: Array<{ key: SortKey; label: string }> = [
  { key: 'employeeName', label: 'Employee Name' },
  { key: 'shiftName', label: 'Shift Name' },
  { key: 'workWeekName', label: 'Work Week' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'endTime', label: 'End Time' },
  { key: 'effectiveDate', label: 'Effective From' },
];

interface EmployeeShift {
  id: string;
  employeeId: string;
  shiftId: string;
  employeeName: string;
  shiftName: string;
  workWeekId?: string | null;
  workWeekName?: string | null;
  startTime: string;
  endTime: string;
  effectiveDate: string;
  isCurrent: boolean;
}

const EmployeeWithShift: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeShift[]>([]);
  const [shifts, setShifts] = useState<Array<{ id: string; shiftName: string }>>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changeTarget, setChangeTarget] = useState<EmployeeShift | null>(null);
  const [newShiftId, setNewShiftId] = useState('');
  const [newWorkWeekId, setNewWorkWeekId] = useState('');
  const [newEffectiveDate, setNewEffectiveDate] = useState('');
  const [workWeeks, setWorkWeeks] = useState<Array<{ id: string; name: string }>>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>('employeeName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await shiftApi.getEmployeesWithShifts();
      setEmployees((data || []).filter((x: EmployeeShift) => x.isCurrent));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    shiftApi
      .getShifts()
      .then((data) => setShifts(data))
      .catch(() => setShifts([]));
  }, []);

  useEffect(() => {
    shiftApi
      .getWorkWeekOptions()
      .then((data) => setWorkWeeks(data))
      .catch(() => setWorkWeeks([]));
  }, []);

  const handleOpenChangeShift = (employee: EmployeeShift) => {
    setChangeTarget(employee);
    setNewShiftId(employee.shiftId);
    setNewWorkWeekId(employee.workWeekId || '');
    setNewEffectiveDate(
      employee.effectiveDate?.split('T')[0] ||
      new Date().toISOString().split('T')[0]
    );
  };

  const handleCloseChangeShift = () => {
    setChangeTarget(null);
    setNewShiftId('');
    setNewWorkWeekId('');
    setNewEffectiveDate('');
  };

  const handleChangeShift = async () => {
    if (!changeTarget || !newShiftId || !newEffectiveDate) {
      setError('Please select a shift and effective date');
      return;
    }

    setActionLoadingId(changeTarget.id);
    setError(null);
    try {
      if (newShiftId !== changeTarget.shiftId) {
        await shiftApi.deleteEmployeeShift(changeTarget.id);
      }

      await shiftApi.assignShiftToEmployee({
        employeeId: changeTarget.employeeId,
        shiftId: newShiftId,
        effectiveDate: newEffectiveDate,
      });

      if (newWorkWeekId) {
        await shiftApi.mapEmployeeToWorkWeek({
          employeeId: changeTarget.employeeId,
          workWeekId: newWorkWeekId,
          effectiveDate: newEffectiveDate,
        });
      }

      handleCloseChangeShift();
      toast.success('Employee mapping updated successfully');
      await fetchEmployees();
    } catch (err: any) {
      const responseData = err.response?.data;
      setError(responseData?.details || responseData?.message || 'Failed to change shift');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRemoveShift = async (employee: EmployeeShift) => {
    if (!window.confirm(`Remove shift for ${employee.employeeName}?`)) {
      return;
    }

    setActionLoadingId(employee.id);
    setError(null);
    try {
      await shiftApi.deleteEmployeeShift(employee.id);
      await fetchEmployees();
    } catch (err: any) {
      const responseData = err.response?.data;
      setError(responseData?.details || responseData?.message || 'Failed to remove shift');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSort = (key: SortKey) => {
    setSortKey(key);
    setSortDirection((current) => (sortKey === key && current === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  };

  const filteredEmployees = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const filtered = employees.filter((e) => e.employeeName.toLowerCase().includes(searchTerm));

    return [...filtered].sort((a, b) => {
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      const result = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });

      return sortDirection === 'asc' ? result : -result;
    });
  }, [employees, search, sortDirection, sortKey]);

  const paginatedEmployees = rowsPerPage === -1
    ? filteredEmployees
    : filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Employee With Shift
      </Typography>

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Search Employee"
          size="small"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
      <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {sortableColumns.map((column) => (
                <TableCell key={column.key}>
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={sortKey === column.key ? sortDirection : 'asc'}
                    onClick={() => handleSort(column.key)}
                  >
                    <strong>{column.label}</strong>
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No employees found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEmployees.map((emp) => (
                <TableRow key={emp.id} hover>
                  <TableCell>{emp.employeeName}</TableCell>
                  <TableCell>{emp.shiftName}</TableCell>
                  <TableCell>{emp.workWeekName || '-'}</TableCell>
                  <TableCell>{formatTime12Hour(emp.startTime)}</TableCell>
                  <TableCell>{formatTime12Hour(emp.endTime)}</TableCell>
                  <TableCell>
                    {emp.effectiveDate
                      ? new Date(emp.effectiveDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="primary"
                      disabled={actionLoadingId === emp.id}
                      onClick={() => handleOpenChangeShift(emp)}
                    >
                      Change Mapping
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={actionLoadingId === emp.id}
                      onClick={() => handleRemoveShift(emp)}
                    >
                      Remove Shift
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredEmployees.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
      </>
      )}

      <Dialog open={!!changeTarget} onClose={handleCloseChangeShift} maxWidth="sm" fullWidth>
        <DialogTitle>Change Employee Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {changeTarget?.employeeName}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Shift</InputLabel>
              <Select
                value={newShiftId}
                label="Shift"
                onChange={(e) => setNewShiftId(e.target.value)}
              >
                <MenuItem value="">Select Shift</MenuItem>
                {shifts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.shiftName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Work Week</InputLabel>
              <Select
                value={newWorkWeekId}
                label="Work Week"
                onChange={(e) => setNewWorkWeekId(e.target.value)}
              >
                <MenuItem value="">Select Work Week</MenuItem>
                {workWeeks.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Effective Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={newEffectiveDate}
              onChange={(e) => setNewEffectiveDate(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeShift}>Cancel</Button>
          <Button onClick={handleChangeShift} variant="contained" disabled={actionLoadingId === changeTarget?.id}>
            {actionLoadingId === changeTarget?.id ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeWithShift;
